import React, { useMemo, useState } from 'react'
import { QueryClient } from 'react-query'
import { Card, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { GetServerSidePropsContext } from 'next'
import { dehydrate } from 'react-query/hydration'

import {
  MainLayout,
  MainLayoutSidebar,
  MainLayoutContent
} from 'src/features/layouts'
import {
  FilterOptionsFactories,
  getFilterOptionsFactories,
  useQueryParamsFilterFactories
} from 'src/features/filters/common'
import {
  FilterFactories,
  FilterFactoriesTag,
  FilterSearchBar,
  FilterSearchBarMobile,
  SORT_ALPHABETICALLY,
  SORT_KEY_CREATED_AT,
  SORT_KEY_RATING,
  TextFieldParamsSort
} from 'src/features/filters'
import {
  profilesApi,
  PROFILE_CITY_KEY,
  PROFILE_COUNTRY_KEY,
  FACTORY_TAG
} from 'src/features/profiles/common'

import { BreadCrumbs } from 'src/features/breadcrumbs/breadcrumbs'
import {
  factoriesSeo,
  factoriesSeoEn,
  // getServiceURL,
  SERVICES_URL,
  useTranslation
} from '@plandi/common'
import {
  SeoTextBlock,
  SEO_TEXTS,
  sharedApi,
  useQuerySeoTexts,
  TRANSLATE
} from 'src/shared'
import { TabsJob } from 'src/features/tabs-job'
import { CreateButtons } from 'src/features/createButtons'
import {
  PROFILE_FACTORY_SPECIALISATIONS_KEY,
  profileFactoryApi,
  useQueryFactoriesList
} from 'src/features/profile-factory/common'
import {
  ProfileFactoryList,
  useProfileFactoryListStyles
} from 'src/features/profile-factory'
import { components } from '@plandi/common/src/generated'
import { FactoryTags, useQueryFactoryTags } from 'src/features/tags'
import { TagTextWrapper } from 'src/features/tag-text-wrapper'

export async function getServerSideProps({
  locale,
  query
}: GetServerSidePropsContext) {
  const queryClient = new QueryClient()
  const queries = getFilterOptionsFactories(query as FilterOptionsFactories)

  const param = {
    tag: queries.tag
  }

  await Promise.all([
    queryClient.prefetchQuery(
      [PROFILE_FACTORY_SPECIALISATIONS_KEY, locale],
      () =>
        profileFactoryApi.getProfileFactorySpecializationsMain(locale ?? 'en')
    ),
    queryClient.prefetchQuery([PROFILE_COUNTRY_KEY, locale], () =>
      profilesApi.getProfileCountries(locale ?? 'en')
    ),
    queryClient.prefetchQuery([PROFILE_CITY_KEY, locale], () =>
      profilesApi.getProfileCities(locale ?? 'en')
    ),
    queryClient.prefetchQuery([SEO_TEXTS, locale], () =>
      sharedApi.getSEOText(locale ?? 'en')
    ),
    queryClient.prefetchQuery(TRANSLATE, () =>
      sharedApi.getTranslate(locale ?? 'en')
    ),
    queryClient.prefetchQuery([FACTORY_TAG, locale], () =>
      profileFactoryApi.getFactoryTags(locale ?? 'en')
    )
  ])

  const tags = await profileFactoryApi.getFactoryTags(locale ?? 'en')
  const currentTag = param.tag
    ? tags.find((tag) => tag.id === Number(param.tag))
    : null

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      locale: locale,
      currentTag
    }
  }
}

const Factories = (props: {
  locale: string
  currentTag?: components['schemas']['ShopTagResource']
}) => {
  const { title, description } = factoriesSeo
  const { titleEn, descriptionEn } = factoriesSeoEn
  const { data: translate } = useTranslation()
  const [itemsView, setItemsView] = useState<'sausage' | 'card'>('card')
  const sortKeys = [SORT_KEY_RATING, SORT_KEY_CREATED_AT, SORT_ALPHABETICALLY]

  const { data: seoTexts } = useQuerySeoTexts()
  const { query } = useQueryParamsFilterFactories()
  const { data: tags } = useQueryFactoryTags()

  const tagId = Number(query.tag)
  const locale = props.locale
  const url = SERVICES_URL

  const changedTag = useMemo(() => {
    if (!tagId) return null
    if (tagId === props.currentTag?.id) return props.currentTag
    return tags?.find((tag) => tag.id === tagId)
  }, [props.currentTag, tagId, tags])

  const classes = useProfileFactoryListStyles()
  const { data } = useQueryFactoriesList()
  const employersCount = data?.total ?? 0

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const isLaptop = useMediaQuery(theme.breakpoints.down(1441))
  const isTablet = useMediaQuery(theme.breakpoints.down(960))

  return (
    <MainLayout
      className="grayColor"
      withFooter
      title={changedTag?.seoTitle ?? props.locale === 'ru' ? title : titleEn}
      description={
        changedTag?.seoDescription ?? props.locale === 'ru'
          ? description
          : descriptionEn
      }
      currentTag={props?.currentTag}
    >
      <div style={{ width: '100%' }}>
        <BreadCrumbs
          breadcrumbInfo={[
            {
              title: translate?.['job']?.['title2'] ?? 'Фриланс и Работа',
              href:
                locale === 'ru'
                  ? `${url.job}/ru/applicants`
                  : `${url.job}/applicants`
            },
            {
              title: translate?.['job-site']?.factories ?? 'Заводы',
              href: ''
            }
          ]}
          styles={{ marginBottom: isDesktop ? 0 : 20 }}
        />
        <TabsJob
          place="factories"
          translate={translate}
          locale={props.locale}
          title={changedTag ?? props.currentTag}
        />
        <FactoryTags />
        {isDesktop ? (
          <FilterSearchBar
            withButton
            style={'modalStyle'}
            typeForSearch={'factory'}
            translate={translate}
          />
        ) : (
          <FilterSearchBarMobile
            typeForSearch={'factory'}
            sortKey={'order'}
            translate={translate}
          >
            <FilterFactories translate={translate} locale={props.locale} />
          </FilterSearchBarMobile>
        )}
        <div style={{ display: 'flex' }}>
          <div>
            {isDesktop && (
              <Typography className={classes.modelAmount}>
                {`${
                  translate?.shop?.['models-page']?.['count-results'] ??
                  'Результатов'
                }: ${employersCount}`}
              </Typography>
            )}
            <MainLayoutSidebar hideMobile isFixed>
              <CreateButtons translate={translate} locale={props.locale} />
              <Card>
                <FilterFactories translate={translate} locale={props.locale} />
              </Card>
            </MainLayoutSidebar>
          </div>
          <MainLayoutContent>
            <FilterFactoriesTag translate={translate} />
            {isTablet && (
              <>
                <CreateButtons translate={translate} locale={props.locale} />
                <Typography className={classes.modelAmount}>
                  {`${
                    translate?.shop?.['models-page']?.['count-results'] ??
                    'Результатов'
                  }: ${employersCount}`}
                </Typography>
              </>
            )}
            {isDesktop && (
              <TextFieldParamsSort
                sortKeys={sortKeys}
                isEmployersPage
                setItemsView={setItemsView}
                queryKey={'order'}
                itemsView={itemsView}
                defaultValue={SORT_KEY_RATING}
                typeForSearch={'factory'}
                style={{
                  width: isLaptop ? '50%' : '38%',
                  marginBottom: isTablet ? 0 : 45,
                  marginTop: isTablet ? 15 : 0
                }}
                translate={translate}
              />
            )}

            <ProfileFactoryList
              itemsView={itemsView}
              translate={translate}
              locale={props.locale}
            />
          </MainLayoutContent>
        </div>
        {tagId &&
        (changedTag?.description || props?.currentTag?.description) ? (
          <TagTextWrapper
            text={changedTag?.description ?? props?.currentTag?.description}
          />
        ) : (
          <SeoTextBlock text={seoTexts?.factory} />
        )}
      </div>
    </MainLayout>
  )
}

export default Factories
